import { AppBar, Body, Button, PageLayout } from 'components/common';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

// §22 별도 동의 체크 글리프(토스 스타일). 체크 시 accent, 미체크 시 회색. 재사용 컴포넌트가 없어 이 화면 한정.
const CheckGlyph: React.FC<{ checked: boolean; large?: boolean }> = ({ checked, large }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={large ? 2.6 : 2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`${large ? 'h-[26px] w-[26px]' : 'h-[22px] w-[22px]'} shrink-0 ${checked ? 'text-base-txt-purple' : 'text-base-txt-tertiary'}`}
  >
    <path d="M4 12.5 9.5 18 20 6" />
  </svg>
);

// 개별 동의 행. 터치 영역 ≥44px, role/aria로 스크린리더 상태 전달.
const ConsentRow: React.FC<{ checked: boolean; onToggle: () => void; label: string }> = ({ checked, onToggle, label }) => (
  <div
    onClick={onToggle}
    role="checkbox"
    aria-checked={checked}
    className="flex min-h-[36px] cursor-pointer items-center gap-3 py-0.5"
  >
    <CheckGlyph checked={checked} />
    <span className="flex-1 typography-body-sm text-base-txt-secondary">{label}</span>
  </div>
);

// 아코디언 헤더의 펼침 표시 chevron. 열리면 180° 회전(색상만으로 상태 전달 금지 → 회전+aria 병행).
const ChevronGlyph: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`h-5 w-5 shrink-0 text-base-txt-tertiary transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// 약관 섹션 아코디언 행(토스/카카오 「약관 동의」 패턴). 하나의 회색 카드 안에 행으로 쌓임 → 접힘, 탭하면 약관 전문 펼침.
// 높이 애니메이션은 grid-rows 0fr→1fr 트릭으로 콘텐츠 높이를 몰라도 부드럽게 전개.
const AccordionSection: React.FC<{ index: number; title: string; paragraphs: string[]; showDivider: boolean }> = ({ index, title, paragraphs, showDivider }) => {
  const [open, setOpen] = useState(false);
  const panelId = `age-doc-panel-${index}`;
  return (
    <div>
      {showDivider && <div className="mx-5 border-t border-base-bg-strong" />}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="block h-auto w-full appearance-none border-0 bg-transparent px-5 py-2.5 text-start"
      >
        {/* h-auto: 전역 `button{height:36px}`(main.css:12963) 누수로 다줄 제목이 36px에 갇혀 겹치던 것을 해제. flex는 안쪽 div가 담당(WebKit <button> flex 한계, main.css:3667). */}
        <div className="flex min-h-[40px] w-full items-center gap-3">
          <span className="flex-1 typography-body-md font-medium text-base-txt-primary" dangerouslySetInnerHTML={{ __html: title }} />
          <ChevronGlyph open={open} />
        </div>
      </button>
      <div
        id={panelId}
        className={`grid transition-all duration-300 ease-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-4 pt-2">
            {paragraphs.map((paragraph, paragraphIndex) => (
              <div
                key={paragraphIndex}
                className="whitespace-pre-line typography-body-sm leading-relaxed text-base-txt-secondary"
                dangerouslySetInnerHTML={{ __html: paragraph }}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PageUnderageDocument: React.FC = () => {
  const location = useLocation();
  // yoti 모드로 진입하면 '다음'에서 준비물 안내 페이지로, 그 외엔 기존 수동 촬영 플로우로.
  const isYoti = (location.state as { mode?: string } | null)?.mode === 'yoti';

  const [isExiting, setIsExiting] = useState(false);

  // §22 별도 동의 — 수집·이용(§15)·민감정보(§23)는 항상, 국외이전(§28-8)은 yoti 경로에서만 필수.
  const [agreeCollect, setAgreeCollect] = useState(false);
  const [agreeSensitive, setAgreeSensitive] = useState(false);
  const [agreeOverseas, setAgreeOverseas] = useState(false);
  const allAgreed = agreeCollect && agreeSensitive && (isYoti ? agreeOverseas : true);
  const toggleAll = () => {
    const next = !allAgreed;
    setAgreeCollect(next);
    setAgreeSensitive(next);
    setAgreeOverseas(next);
  };

  const title = brsc.age_verification_title;
  const contents = [
    { title: brsc.age_verification_title_1, content: [brsc.age_verification_contents_1, brsc.age_verification_contents_2] },
    { title: brsc.age_verification_title_2, content: [brsc.age_verification_contents_3, brsc.age_verification_contents_4] },
    { title: brsc.age_verification_title_3, content: [brsc.age_verification_contents_5, brsc.age_verification_contents_6] },
    { title: brsc.age_verification_title_overseas, content: [brsc.age_verification_overseas_1, brsc.age_verification_overseas_2, brsc.age_verification_overseas_3, brsc.age_verification_overseas_4] },
    { title: brsc.age_verification_title_4, content: [brsc.age_verification_contents_7] },
    { title: brsc.age_verification_title_5, content: [brsc.age_verification_contents_8] },
    { title: brsc.age_verification_title_6, content: [brsc.age_verification_contents_9, brsc.age_verification_contents_10] },
  ];

  const handleButtonClick = () => {
    // §22 필수 동의 미완료면 진행 불가(버튼 비활성). 스크롤 게이트는 아코디언 전환으로 제거됨.
    if (!allAgreed) {
      return;
    }

    // yoti 모드: 준비물 안내 페이지로 이동. 세션 생성은 그 페이지의 CTA에서 수행.
    if (isYoti) {
      Transition.showPage(Def.PAGE_UNDERAGE_YOTI_PREPARE);
      return;
    }

    // 비-yoti(수동) 경로는 기존 페이드 전환 유지.
    setIsExiting(true);
    setTimeout(() => {
      Transition.showPage(Def.PAGE_UNDERAGE_VERIFICATION);
    }, 500);
  };

  return (
    <PageLayout>
      <div className={`transition-opacity duration-500 ${isExiting ? 'opacity-0' : 'opacity-100'}`}>
        <AppBar onBack={() => Transition.clearNavigateAndHistory()} />
        <Body className="flex flex-col pb-[60px]">
          <div className="no-scrollbar flex-1 overflow-y-auto px-6 pb-[60px]">
            <h1 className="mb-4 typography-title-md text-base-txt-primary">{title}</h1>
            {/* 인트로는 한 단락으로 — 원문의 연속 <br> 문단 구분을 공백으로 접어 하나의 문단처럼 표시(i18n 원문은 불변). */}
            <div className="mb-5 whitespace-pre-line typography-body-sm leading-normal text-base-txt-secondary" dangerouslySetInnerHTML={{ __html: brsc.age_verification_contents.replace(/(?:<br\s*\/?>\s*){2,}/gi, ' ') }}></div>

            {/* 약관 아코디언 카드. 라운드는 작게(rounded-lg). overflow-hidden으로 라운드·아코디언 동시 클립. 위아래 py로 첫/마지막 행 여백 확보. */}
            <div className="mb-3 overflow-hidden rounded-lg bg-base-bg-subtle py-1.5">
              {contents.map((item, index) => (
                <AccordionSection key={index} index={index} title={item.title} paragraphs={item.content} showDivider={index > 0} />
              ))}
            </div>

            {/* §22 별도 동의 — '약관 열람' 그룹과 다른 그룹이므로 별도 회색 카드로 분리(토스: 관련 요소는 가깝게, 다른 요소는 멀리). 카드 사이 gap(위 카드 mb-3)이 '보안 조치'와 '전체 동의하기'를 뚜렷이 구분. */}
            <div className="mb-4 overflow-hidden rounded-lg bg-base-bg-subtle py-1.5">
              {/* 토스 스타일: 전체 동의 마스터 + 개별 행. 버튼 활성 조건. */}
              <div
                onClick={toggleAll}
                role="checkbox"
                aria-checked={allAgreed}
                className="flex min-h-[48px] cursor-pointer items-center gap-3 px-5 py-2.5"
              >
                <CheckGlyph checked={allAgreed} large />
                <span className="flex-1 typography-headline-lg text-base-txt-primary">{brsc.age_verification_agree_all}</span>
              </div>
              <div className="mx-5 border-t border-base-bg-strong" />
              <div className="px-5 py-1.5">
                <ConsentRow checked={agreeCollect} onToggle={() => setAgreeCollect((v) => !v)} label={brsc.age_verification_agree_collect} />
                <ConsentRow checked={agreeSensitive} onToggle={() => setAgreeSensitive((v) => !v)} label={brsc.age_verification_agree_sensitive} />
                {isYoti && (
                  <ConsentRow checked={agreeOverseas} onToggle={() => setAgreeOverseas((v) => !v)} label={brsc.age_verification_agree_overseas} />
                )}
              </div>
            </div>
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex justify-center p-6" style={{ paddingBottom: 'calc(var(--navigation-bar-height) + 16px)' }}>
            <Button
              style={{
                text: brsc.btn_ok,
                color: allAgreed ? 'btn-bg-secondary' : 'btn-bg-disabled',
                fontColor: allAgreed ? 'btn-txt-secondary' : 'btn-txt-disabled',
              }}
              onClick={handleButtonClick}
              className="max-w-[420px]"
            />
          </div>
        </Body>
      </div>
    </PageLayout>
  );
};

export default PageUnderageDocument;
